import { FormJsonSerializer } from '../lib/form-json-serializer';
import { IFormPageData } from '../lib/models/form-page-data';
import { FieldJsonData } from '../lib/models/element-types';
import { formStylesDataToCssVariables } from '../lib/models/styles/form-styles-css.utils';
import { FormLoaderService, FormData, FormNavigationPosition, FormNavigationType } from './form-loader.service';
import { FormValidator } from './form-validator';
import { DateFieldPreparer, initializeDateFields } from '../lib/preparers/date-field-preparer';
import { DropdownFieldPreparer, initializeDropdownFields } from '../lib/preparers/dropdown-field-preparer';
import { SignatureFieldPreparer, initializeSignatureFields, type SignatureFieldPreparerOptions } from '../lib/preparers/signature-field-preparer';
import { FileFieldPreparer, FileFieldPreparerOptions, initializeFileFields } from '../lib/preparers/file-field-preparer';
import { TranslateUtils } from '../lib/utils/translate.utils';
import { type VariablesMap } from '../lib/utils/variable-replacement.utils';
import { Base64Utils } from '../lib/utils/base-64-utils';
import { isNullOrWhiteSpace } from '../lib/shared/utils/string';

export interface FormFieldValue {
    fieldId: string;
    fieldType: string;
    label: string;
    value: any;
    crmMapping?: CrmMappingInfo | null;
}

export interface CrmMappingInfo {
    mixedType: 'CustomField' | 'ContactField';
    type: string;
    name: string;
    id?: string;
    systemName?: string;
}

export interface FormFieldMetadata {
    fieldId: string;
    fieldType: string;
    label: string;
    required: boolean;
    crmMapping?: CrmMappingInfo | null;
    options?: { value: string; label: string }[];  // For dropdown, radio, checkbox
}

export class TruFormElement extends HTMLElement {
    private static readonly OBSERVED_ATTRIBUTES = ['form-id', 'api-url', 'theme', 'language', 'styles-url', 'dir', 'gdpr-enabled', 'privacy-policy-url', 'hidden-fields', 'variables'];
    private static fontsLoaded = false;

    private readonly serializer = new FormJsonSerializer();
    private readonly formLoader = new FormLoaderService();
    private readonly loadedStylesheets = new Set<string>();
    private readonly validator = new FormValidator();

    private shadow: ShadowRoot;
    private formContainer: HTMLFormElement | null = null;
    private formData: FormData | null = null;
    private additional: string | null = null;
    private isLoading = false;
    private lastFormWidth: number | null = null;
    private formResizeObserver?: ResizeObserver;
    private viewportResizeObserver?: ResizeObserver;
    private scrollState = {
        canScrollLeft: false,
        canScrollRight: false,
        isAtStart: true,
        isAtEnd: false
    };

    private get category(): string | undefined {
        return this.formData?.settings?.category;
    }

    private currentPageIndex = 0;
    private pageElements: HTMLElement[] = [];
    private recaptchaV2Container: HTMLElement | null = null;
    private recaptchaV2WidgetId: number | null = null;

    // Navigation menu left/right controls
    private resizeObserver: ResizeObserver | null = null;

    constructor() {
        super();
        this.shadow = this.attachShadow({ mode: 'open' });
    }

    public static get observedAttributes(): string[] {
        return TruFormElement.OBSERVED_ATTRIBUTES;
    }

    public connectedCallback(): void {
        this.render();
        this.updateDirection(this.getAttribute('dir'));
        this.loadStylesIfNeeded();

        const formId = this.getAttribute('form-id');
        if (formId) {
            this.loadForm(formId);
        }
    }

    public attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
        if (name === 'dir') {
            this.updateDirection(newValue);
        } else if (name === 'form-id' && newValue && newValue !== oldValue
            && this.isConnected && !this.formData && !this.isLoading) {
            this.loadForm(newValue);
        } else if (name === 'hidden-fields' && newValue !== oldValue) {
            this.applyHiddenFieldOverrides();
        }
    }

    private updateDirection(dir: string | null): void {
        const mainEl = this.shadow.querySelector('#main');
        if (mainEl) {
            if (dir) {
                mainEl.setAttribute('dir', dir);
            } else {
                mainEl.removeAttribute('dir');
            }
        }
    }

    public async loadForm(formId: string): Promise<void> {
        if (this.isLoading) {
            return;
        }

        this.isLoading = true;
        this.dispatchEvent(new CustomEvent('tru-form-loading', { detail: { formId } }));

        try {
            const apiUrl = this.getAttribute('api-url') || '';
            this.formData = await this.formLoader.loadForm(formId, apiUrl);
            await this.renderForm();
            this.dispatchEvent(new CustomEvent('tru-form-loaded', { detail: { formId, category: this.category } }));
        } catch (error: any) {
            const errorCode = error.message || 'form-load-error';
            const loadErrorEvent = new CustomEvent('tru-form-load-error', {
                detail: {
                    formId,
                    category: this.category,
                    error
                },
                cancelable: true
            });
            this.dispatchEvent(loadErrorEvent);

            if (!loadErrorEvent.defaultPrevented) {
                this.displayLoadError(errorCode);
            }

            console.error('[TruForm] Failed to load form:', error);
        } finally {
            this.isLoading = false;
        }
    }

    public async setFormData(data: FormData): Promise<void> {
        this.formData = data;
        await this.renderForm();
        // Dispatch loaded event when using setFormData directly
        this.dispatchEvent(new CustomEvent('tru-form-loaded', {
            detail: { formId: data.formId, category: this.category }
        }));
    }

    public getFormData(): FormData | null {
        return this.formData;
    }

    public setAdditional(value: unknown): void {
        if (value === null || value === undefined || (typeof value === 'string' && isNullOrWhiteSpace(value))) {
            this.additional = null;
            return;
        }

        try {
            this.additional = typeof value === 'string'
                ? value
                : JSON.stringify(value);
        } catch {
            console.warn('[TruForm] Additional ignored: value cannot be serialized');
            this.additional = null;
        }
    }

    public setAdditionalData(value: unknown): void {
        this.setAdditional(value);
    }

    /**
     * Get variables from the 'variables' attribute as a key-value object
     */
    private getVariables(): VariablesMap {
        const attr = this.getAttribute('variables');
        if (!attr) {
            return {};
        }

        try {
            const parsed = JSON.parse(attr);
            if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
                return parsed;
            }
            console.warn('[TruForm] Variables attribute must be a JSON object');
            return {};
        } catch {
            console.warn('[TruForm] Invalid JSON in variables attribute');
            return {};
        }
    }

    /**
     * Get metadata for all form fields (for pre-filling)
     */
    public getFieldsMetadata(): FormFieldMetadata[] {
        if (!this.formContainer) {
            return [];
        }

        const fields: FormFieldMetadata[] = [];
        const fieldElements = this.formContainer.querySelectorAll<HTMLElement>('[data-element="field"]');

        fieldElements.forEach(fieldEl => {
            const fieldId = fieldEl.id;
            if (!fieldId) {
                return;
            }

            const jsonAttr = fieldEl.getAttribute('data-element-json');
            let fieldJsonData: FieldJsonData | null = null;

            if (jsonAttr) {
                try {
                    fieldJsonData = JSON.parse(jsonAttr) as FieldJsonData;
                } catch (e) {
                    console.warn('[TruForm] Failed to parse field JSON:', e);
                }
            }

            const fieldType = fieldEl.dataset['field'] || fieldJsonData?.type || 'unknown';
            const input = fieldEl.querySelector<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(
                'input:not([type="hidden"]), textarea, select'
            );

            const metadata: FormFieldMetadata = {
                fieldId,
                fieldType,
                label: fieldJsonData?.label || '',
                required: input?.required || fieldEl.classList.contains('required'),
                crmMapping: fieldJsonData?.crmFieldData || null
            };

            // Add options for select/radio/checkbox fields
            if (fieldType === 'dropdown' || fieldType === 'radio' || fieldType === 'checkbox') {
                metadata.options = this.extractFieldOptions(fieldEl, fieldType);
            }

            fields.push(metadata);
        });

        return fields;
    }

    /**
     * Set value for a single field by fieldId
     */
    public setFieldValue(fieldId: string, value: any): boolean {
        if (!this.formContainer) {
            return false;
        }

        const fieldEl = this.formContainer.querySelector<HTMLElement>(`#${fieldId}`);
        if (!fieldEl) {
            console.warn(`[TruForm] Field not found: ${fieldId}`);
            return false;
        }

        const fieldType = fieldEl.dataset['field'];
        return this.setFieldInputValue(fieldEl, fieldType, value);
    }

    /**
     * Set values for multiple fields at once
     * @param values - Object with fieldId as key and value as value, or array of {fieldId, value}
     */
    public setFieldValues(values: Record<string, any> | Array<{ fieldId: string; value: any }>): void {
        const entries = Array.isArray(values)
            ? values
            : Object.entries(values).map(([fieldId, value]) => ({ fieldId, value }));

        entries.forEach(({ fieldId, value }) => {
            this.setFieldValue(fieldId, value);
        });
    }

    /**
     * Set readonly for a single field by fieldId
     */
    public setFieldReadOnly(fieldId: string, readOnly: boolean): boolean {
        if (!this.formContainer) {
            return false;
        }

        const fieldEl = this.formContainer.querySelector<HTMLElement>(`#${fieldId}`);
        if (!fieldEl) {
            console.warn(`[TruForm] Field not found: ${fieldId}`);
            return false;
        }

        return this.setFieldInputReadOnly(fieldEl, fieldEl.dataset['field'], readOnly);
    }

    /**
     * Set or clear error for a single field
     * @param fieldId - Field ID
     * @param errorMessage - Error message to display. If empty or not provided, clears the error.
     * @returns true if field was found
     */
    public setFieldError(fieldId: string, errorMessage?: string): boolean {
        if (!this.formContainer) {
            return false;
        }

        const fieldEl = this.formContainer.querySelector<HTMLElement>(`#${fieldId}`);
        if (!fieldEl) {
            console.warn(`[TruForm] Field not found: ${fieldId}`);
            return false;
        }

        const fieldType = fieldEl.dataset['field'];
        const input = this.getFieldInputElement(fieldEl, fieldType);

        if (!input) {
            console.warn(`[TruForm] Input not found in field: ${fieldId}`);
            return false;
        }

        if (errorMessage) {
            // Show error
            const label = this.getFieldLabel(fieldEl);
            this.validator.showFieldError({
                fieldId,
                fieldName: label,
                message: errorMessage,
                element: input
            });
        } else {
            // Clear error
            this.validator.clearErrors([input]);
        }

        return true;
    }

    /**
     * Set or clear errors for multiple fields at once
     * @param errors - Object with fieldId as key and error message as value, or array of {fieldId, errorMessage}
     *                 Empty string or undefined value clears the error for that field
     */
    public setFieldErrors(errors: Record<string, string | undefined> | Array<{ fieldId: string; errorMessage?: string }>): void {
        const entries = Array.isArray(errors)
            ? errors
            : Object.entries(errors).map(([fieldId, errorMessage]) => ({ fieldId, errorMessage }));

        entries.forEach(({ fieldId, errorMessage }) => {
            this.setFieldError(fieldId, errorMessage);
        });
    }

    /**
     * Clear error for a single field
     * @param fieldId - Field ID
     * @returns true if field was found
     */
    public clearFieldError(fieldId: string): boolean {
        return this.setFieldError(fieldId, '');
    }

    /**
     * Clear all field errors in the form
     */
    public clearAllFieldErrors(): void {
        if (!this.formContainer) {
            return;
        }
        this.validator.clearAllErrors(this.formContainer);
    }

    /**
     * Reset form to initial state (as if just loaded)
     * - Clears all field values
     * - Clears all validation errors
     * - Resets to first page (multi-page forms)
     * - Resets CAPTCHA if present
     */
    public async reset(): Promise<void> {
        if (!this.formContainer) {
            return;
        }

        // Clear all validation errors first
        this.validator.clearAllErrors(this.formContainer);

        // Clear submission errors
        this.clearSubmissionError();

        // Reset all text/email/tel/url/number/textarea fields
        const textInputs = this.formContainer.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>(
            'input:not([type="hidden"]):not([type="checkbox"]):not([type="radio"]):not([type="file"]), textarea'
        );
        textInputs.forEach(input => {
            input.value = '';
        });

        // Reset all select fields to first option
        const selects = this.formContainer.querySelectorAll<HTMLSelectElement>('select');
        selects.forEach(select => {
            select.selectedIndex = 0;
        });

        // Uncheck all checkboxes
        const checkboxes = this.formContainer.querySelectorAll<HTMLInputElement>('input[type="checkbox"]');
        checkboxes.forEach(checkbox => {
            checkbox.checked = false;
        });

        // Uncheck all radio buttons
        const radios = this.formContainer.querySelectorAll<HTMLInputElement>('input[type="radio"]');
        radios.forEach(radio => {
            radio.checked = false;
        });

        // Reset file inputs
        const fileInputs = this.formContainer.querySelectorAll<HTMLInputElement>('input[type="file"]');
        fileInputs.forEach(fileInput => {
            fileInput.value = '';
            const fileSelectText = fileInput.parentElement?.querySelector('.file-select-text');
            if (fileSelectText) {
                fileSelectText.textContent = TranslateUtils.UPLOAD_FILE;
            }
        });

        // Reset password strength validation UI
        const strengthContainers = this.formContainer.querySelectorAll<HTMLElement>('.password-strength-rules');
        strengthContainers.forEach(container => {
            container.classList.add('untouched');
            const rules = container.querySelectorAll('[data-rule]');
            rules.forEach(rule => rule.classList.remove('valid'));
        });

        // Reset CAPTCHA
        await this.resetCaptcha();

        // Reset to first page for multi-page forms
        if (this.pageElements.length > 1 && this.currentPageIndex !== 0) {
            this.goToPage(0);
        }

        // Dispatch reset event
        this.dispatchEvent(new CustomEvent('tru-form-reset', {
            detail: { formId: this.formData?.formId, category: this.category }
        }));
    }

    private getFieldInputElement(fieldEl: HTMLElement, fieldType?: string): HTMLElement | null {
        if (fieldType === 'checkbox') {
            const optionsContainer = fieldEl.querySelector<HTMLElement>('.options');
            return optionsContainer || fieldEl.querySelector<HTMLInputElement>('input[type="checkbox"]');
        }
        if (fieldType === 'radio') {
            const optionsContainer = fieldEl.querySelector<HTMLElement>('.options');
            return optionsContainer || fieldEl.querySelector<HTMLInputElement>('input[type="radio"]');
        }
        if (fieldType === 'file') {
            return fieldEl.querySelector<HTMLElement>('.file-select-label') ||
                   fieldEl.querySelector<HTMLInputElement>('input[type="file"]');
        }
        return fieldEl.querySelector<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(
            'input:not([type="hidden"]):not([type="checkbox"]):not([type="radio"]), textarea, select'
        );
    }

    private getFieldLabel(fieldEl: HTMLElement): string {
        const label = fieldEl.querySelector('label.label, legend.label');
        return label?.textContent?.replace('*', '').trim() || '';
    }

    private extractFieldOptions(fieldEl: HTMLElement, fieldType: string): { value: string; label: string }[] {
        const options: { value: string; label: string }[] = [];

        if (fieldType === 'dropdown') {
            const selectOptions = fieldEl.querySelectorAll<HTMLOptionElement>('select option');
            selectOptions.forEach(opt => {
                if (opt.value) {
                    options.push({ value: opt.value, label: opt.textContent || opt.value });
                }
            });
        } else if (fieldType === 'radio' || fieldType === 'checkbox') {
            const inputs = fieldEl.querySelectorAll<HTMLInputElement>(`input[type="${fieldType === 'radio' ? 'radio' : 'checkbox'}"]`);
            inputs.forEach(input => {
                const label = fieldEl.querySelector<HTMLLabelElement>(`label[for="${input.id}"]`);
                options.push({
                    value: input.value,
                    label: label?.textContent?.trim() || input.value
                });
            });
        }

        return options;
    }

    private setFieldInputValue(fieldEl: HTMLElement, fieldType: string | undefined, value: any): boolean {
        switch (fieldType) {
            case 'signature': {
                const preparer = this.signatureFieldPreparers.find(p => p.element === fieldEl);
                if (preparer && typeof value === 'string') {
                    preparer.loadFromDataURL(value);
                    return true;
                }
                return false;
            }
            case 'checkbox': {
                const checkboxes = fieldEl.querySelectorAll<HTMLInputElement>('input[type="checkbox"]');
                const values = Array.isArray(value) ? value : [value];
                checkboxes.forEach(cb => {
                    cb.checked = values.includes(cb.value);
                });
                return true;
            }
            case 'radio': {
                const radios = fieldEl.querySelectorAll<HTMLInputElement>('input[type="radio"]');
                radios.forEach(radio => {
                    radio.checked = radio.value === value;
                });
                return true;
            }
            case 'dropdown': {
                const select = fieldEl.querySelector<HTMLSelectElement>('select');
                if (select) {
                    select.value = value;
                    return true;
                }
                return false;
            }
            default: {
                const input = fieldEl.querySelector<HTMLInputElement | HTMLTextAreaElement>(
                    'input:not([type="hidden"]):not([type="checkbox"]):not([type="radio"]), textarea'
                );
                if (input) {
                    input.value = value ?? '';
                    return true;
                }
                return false;
            }
        }
    }

    private setFieldInputReadOnly(fieldEl: HTMLElement, fieldType: string | undefined, readOnly: boolean): boolean {
        switch (fieldType) {
            case 'checkbox':
                fieldEl.querySelectorAll<HTMLInputElement>('input[type="checkbox"]')
                    .forEach(input => input.disabled = readOnly);
                return true;

            case 'radio':
                fieldEl.querySelectorAll<HTMLInputElement>('input[type="radio"]')
                    .forEach(input => input.disabled = readOnly);
                return true;

            case 'dropdown': {
                const select = fieldEl.querySelector<HTMLSelectElement>('select');
                if (!select) {
                    return false;
                }

                select.disabled = readOnly;
                return true;
            }
            
            case 'signature':                
                return false;

            default: {
                const input = fieldEl.querySelector<HTMLInputElement | HTMLTextAreaElement>(
                    'input:not([type="hidden"]):not([type="checkbox"]):not([type="radio"]), textarea'
                );

                if (!input) {
                    return false;
                }

                input.readOnly = readOnly;
                return true;
            }
        }
    }

    public async submit(): Promise<void> {
        if (!this.formContainer || this.isLoading) return;

        const formId = this.formData?.formId || this.getAttribute('form-id');
        if (!formId) {
            console.error('[TruForm] No form ID available for submission');
            return;
        }

        // If multi-page form and not on last page, go to next page instead
        if (!this.isLastPage()) {
            this.goToNextPage();
            return;
        }

        // Validate form before submission
        const validationResult = this.validator.validate(this.formContainer);
        if (!validationResult.isValid) {
            const errors = validationResult.errors;

            this.validator.showErrors(errors);

            if (this.pageElements.length > 0) {
                const targetPageIndex = errors
                    .map(e => this.pageElements.findIndex(page => page.contains(e.element)))
                    .filter(index => index !== -1)
                    .reduce((min, index) => Math.min(min, index), Number.POSITIVE_INFINITY);

                if (Number.isFinite(targetPageIndex) && targetPageIndex !== this.currentPageIndex) {
                    this.goToPage(targetPageIndex);
                }
            }

            const currentPage = this.pageElements[this.currentPageIndex];
            const errorsOnCurrentPage = currentPage
                ? errors.filter(e => currentPage.contains(e.element))
                : errors;
            this.validator.scrollToFirstError(errorsOnCurrentPage.length > 0 ? errorsOnCurrentPage : errors);

            this.dispatchEvent(new CustomEvent('tru-form-validation-error', {
                detail: { formId, category: this.category, errors: validationResult.errors }
            }));
            return;
        }

        // Clear any previous validation errors
        this.validator.clearAllErrors(this.formContainer);

        // Prepare captcha before collecting form data
        const captchaPrepared = await this.prepareCaptcha();
        if (!captchaPrepared) {
            this.dispatchEvent(new CustomEvent('tru-form-error', {
                detail: { formId, category: this.category, error: new Error('captcha-unprepared') }
            }));
            return;
        }

        const formDataToSend = new FormData(this.formContainer);

        // Add __draft field if submitting a draft form (preview mode)
        if (this.formData?.isDraft) {
            formDataToSend.append('__draft', 'true');
        }

        // Collect structured field data
        const fields = this.collectFieldValues();

        // Dispatch submit event (cancelable)
        const submitEvent = new CustomEvent('tru-form-submit', {
            detail: { formId, category: this.category, fields },
            cancelable: true
        });
        this.dispatchEvent(submitEvent);

        // If event was cancelled, don't proceed with submission
        if (submitEvent.defaultPrevented) {
            return;
        }

        // Add system fields required by backend
        this.appendSystemFields(formDataToSend);

        this.applyStructuredFieldValuesToFormData(formDataToSend, fields);

        this.isLoading = true;
        this.setLoadingState(true);

        const baseUrl = this.getApiBaseUrl();
        const cleanFormId = formId.replace(/-/g, '');

        try {
            this.clearSubmissionError();

            const response = await fetch(`${baseUrl}/api/public/forms/${cleanFormId}/response`, {
                method: 'POST',
                body: formDataToSend,
                credentials: 'same-origin'
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.code || `HTTP ${response.status}`);
            }

            const responseId = await response.json();

            this.dispatchEvent(new CustomEvent('tru-form-success', {
                detail: { formId, category: this.category, responseId }
            }));

            // Show thank you page
            await this.showThankYouPage(baseUrl, cleanFormId, responseId);

        } catch (error: any) {
            if (error.message === 'received-response-from-draft') {
                this.dispatchEvent(new CustomEvent('tru-form-success', {
                    detail: { formId, category: this.category, responseId: null, isDraft: true }
                }));
                await this.showThankYouPage(baseUrl, cleanFormId);
                return;
            }

            this.displaySubmissionError(error.message);

            this.dispatchEvent(new CustomEvent('tru-form-error', {
                detail: { formId, category: this.category, error }
            }));
        } finally {
            this.isLoading = false;
            this.setLoadingState(false);
        }
    }

    private async prepareCaptcha(): Promise<boolean> {
        if (!this.formContainer) return true;

        const captchaWidget = this.formContainer.querySelector<HTMLElement>('[data-widget="captcha"]');
        if (!captchaWidget) return true;

        const captchaType = captchaWidget.getAttribute('data-captcha-type');

        if (captchaType === 'googleReCaptchaV3') {
            return this.prepareGoogleReCaptchaV3(captchaWidget);
        }

        if (captchaType === 'googleReCaptcha') {
            return this.prepareGoogleReCaptchaV2(captchaWidget);
        }

        if (captchaType === 'truCaptcha') {
            return this.prepareTruCaptcha(captchaWidget);
        }

        return true;
    }

    private async resetCaptcha(): Promise<void> {
        if (!this.formContainer) return;

        const captchaWidget = this.formContainer.querySelector<HTMLElement>('[data-widget="captcha"]');
        if (!captchaWidget) return;

        const captchaType = captchaWidget.getAttribute('data-captcha-type');

        if (captchaType === 'truCaptcha') {
            const textInput = captchaWidget.querySelector<HTMLInputElement>('.tru-captcha-control');
            if (textInput) {
                textInput.value = '';
            }

            const refresh = (captchaWidget as any).truCaptchaRefresh;
            if (typeof refresh === 'function') {
                await refresh();
            }
        } else if (captchaType === 'googleReCaptcha') {
            const grecaptcha = (window as any).grecaptcha;
            if (grecaptcha?.reset && this.recaptchaV2WidgetId !== null) {
                grecaptcha.reset(this.recaptchaV2WidgetId);
            }
            this.injectRecaptchaToken(captchaWidget, '');
        }
    }

    private async prepareGoogleReCaptchaV3(widget: HTMLElement): Promise<boolean> {
        const siteKey = widget.getAttribute('data-recaptcha-site-key');
        if (!siteKey) {
            console.error('[TruForm] reCAPTCHA V3: site key is not defined');
            return false;
        }

        const elementContent = widget.querySelector<HTMLElement>('.element-content');
        if (!elementContent) {
            return false;
        }

        try {
            const grecaptcha = (window as any).grecaptcha;
            if (!grecaptcha) {
                console.error('[TruForm] reCAPTCHA V3: grecaptcha is not loaded');
                return false;
            }

            const token: string = await new Promise((resolve, reject) => {
                const timeout = setTimeout(() => reject(new Error('Timeout')), 5000);
                Promise.resolve(grecaptcha.execute(siteKey, { action: 'form_submit' }))
                    .then((result: string) => { clearTimeout(timeout); resolve(result); })
                    .catch((err: any) => { clearTimeout(timeout); reject(err); });
            });

            let textarea = elementContent.querySelector<HTMLTextAreaElement>('textarea[name="g-recaptcha-response"]');
            if (!textarea) {
                textarea = document.createElement('textarea');
                textarea.hidden = true;
                textarea.name = 'g-recaptcha-response';
                elementContent.appendChild(textarea);
            }

            textarea.value = token;
            return true;
        } catch (e) {
            console.error('[TruForm] reCAPTCHA V3: execution failed', e);
            return false;
        }
    }

    private initializeReCaptchaV2Checkbox(): void {
        if (!this.formContainer) {
            return;
        }

        const widget = this.formContainer.querySelector<HTMLElement>('[data-widget="captcha"][data-captcha-type="googleReCaptcha"]');
        if (!widget) {
            return;
        }

        const isInvisible = widget.getAttribute('data-recaptcha-invisible') === 'true';
        if (isInvisible) {
            return;
        }

        const siteKey = widget.getAttribute('data-recaptcha-site-key') || '';
        const theme = widget.getAttribute('data-recaptcha-theme') || 'light';

        if (!siteKey) {
            return;
        }

        const recaptchaWrapper = widget.querySelector<HTMLElement>('.recaptcha-wrapper');
        if (recaptchaWrapper) {
            const slot = document.createElement('slot');
            slot.name = 'recaptcha-v2';
            recaptchaWrapper.innerHTML = '';
            recaptchaWrapper.appendChild(slot);
        }

        const lightContainer = document.createElement('div');
        lightContainer.classList.add('tru-form-recaptcha-container');
        lightContainer.setAttribute('slot', 'recaptcha-v2');

        this.appendChild(lightContainer);
        this.recaptchaV2Container = lightContainer;

        this.loadReCaptchaScript().then(() => {
            const grecaptcha = (window as any).grecaptcha;
            if (!grecaptcha?.render) {
                return;
            }

            try {
                this.recaptchaV2WidgetId = grecaptcha.render(lightContainer, {
                    sitekey: siteKey,
                    theme,
                    callback: (token: string) => {
                        this.injectRecaptchaToken(widget, token);
                    },
                    'expired-callback': () => {
                        this.injectRecaptchaToken(widget, '');
                    }
                });
            } catch (e) {
                console.warn('[TruForm] reCAPTCHA V2 render:', e);
            }
        });
    }

    private loadReCaptchaScript(): Promise<void> {
        const gc = (window as any).grecaptcha;
        if (gc?.render) {
            return Promise.resolve();
        }

        return new Promise(resolve => {
            if (document.querySelector('script[src*="recaptcha/api.js"]')) {
                const check = setInterval(() => {
                    if ((window as any).grecaptcha?.render) {
                        clearInterval(check);
                        resolve();
                    }
                }, 100);
                setTimeout(() => { clearInterval(check); resolve(); }, 10000);
                return;
            }

            const script = document.createElement('script');
            script.src = 'https://www.google.com/recaptcha/api.js?render=explicit&hl=en';
            script.async = true;
            script.defer = true;
            script.onload = () => {
                const check = setInterval(() => {
                    if ((window as any).grecaptcha?.render) {
                        clearInterval(check);
                        resolve();
                    }
                }, 100);
                setTimeout(() => { clearInterval(check); resolve(); }, 5000);
            };
            document.head.appendChild(script);
        });
    }

    private async prepareGoogleReCaptchaV2(widget: HTMLElement): Promise<boolean> {
        const isInvisible = widget.getAttribute('data-recaptcha-invisible') === 'true';
        if (!isInvisible) {
            const token = widget.querySelector<HTMLTextAreaElement>('textarea[name="g-recaptcha-response"]')?.value;
            if (!token) {
                this.recaptchaV2Container?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                return false;
            }
            return true;
        }

        const grecaptcha = await this.waitForGrecaptcha();
        if (!grecaptcha) {
            console.error('[TruForm] reCAPTCHA V2: grecaptcha failed to load');
            return false;
        }

        const siteKey = widget.getAttribute('data-recaptcha-site-key') || '';
        const theme = widget.getAttribute('data-recaptcha-theme') || 'light';

        const lightContainer = document.createElement('div');
        lightContainer.style.cssText = 'position:fixed;bottom:0;right:0;z-index:2147483647;';
        document.body.appendChild(lightContainer);

        try {
            return await new Promise<boolean>(resolve => {
                const timeout = setTimeout(() => {
                    console.error('[TruForm] reCAPTCHA V2 invisible: execute timeout');
                    lightContainer.remove();
                    resolve(false);
                }, 10000);

                const widgetId = grecaptcha.render(lightContainer, {
                    sitekey: siteKey,
                    theme,
                    size: 'invisible',
                    badge: 'inline',
                    callback: (token: string) => {
                        clearTimeout(timeout);
                        this.injectRecaptchaToken(widget, token);
                        lightContainer.remove();
                        resolve(!!token);
                    }
                });

                grecaptcha.execute(widgetId);
            });
        } catch (e) {
            console.error('[TruForm] reCAPTCHA V2: render failed', e);
            lightContainer.remove();
            return false;
        }
    }

    private injectRecaptchaToken(widget: HTMLElement, token: string): void {
        let textarea = widget.querySelector<HTMLTextAreaElement>('textarea[name="g-recaptcha-response"]');
        if (!textarea) {
            textarea = document.createElement('textarea');
            textarea.hidden = true;
            textarea.name = 'g-recaptcha-response';
            widget.appendChild(textarea);
        }
        textarea.value = token;
    }

    private prepareTruCaptcha(widget: HTMLElement): boolean {
        const textInput = widget.querySelector<HTMLInputElement>('.tru-captcha-control');
        if (!textInput) {
            console.error('[TruForm] TruCaptcha: input field not found');
            return false;
        }

        const value = textInput.value.trim();
        if (!value) {
            // Scroll to captcha for user attention
            widget.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return false;
        }

        return true;
    }

    private waitForGrecaptcha(timeout = 10000): Promise<any> {
        const gc = (window as any).grecaptcha;
        if (gc?.render) {
            return Promise.resolve(gc);
        }

        return new Promise(resolve => {
            const timeoutId = setTimeout(() => resolve(null), timeout);
            const interval = setInterval(() => {
                const g = (window as any).grecaptcha;
                if (g?.render) {
                    clearTimeout(timeoutId);
                    clearInterval(interval);
                    resolve(g);
                }
            }, 100);
        });
    }

    private async showThankYouPage(baseUrl: string, formId: string, responseId?: string): Promise<void> {
        if (this.formData?.settings?.submitAction === 'None') {
            await this.resetCaptcha();
            return;
        }

        if (this.formData?.settings?.submitAction === 'Redirect' && this.formData?.settings?.redirectUrl) {
            window.location.href = this.formData.settings.redirectUrl;
            return;
        }

        if (this.formData?.thankYouPage) {
            this.renderThankYouPage(this.formData.thankYouPage);
            return;
        }

        try {
            const thankYouUrl = this.formData?.isDraft
                ? `${baseUrl}/host/forms/${formId}/preview/thank-you`
                : `${baseUrl}/host/forms/${formId}/thank-you?responseId=${responseId}&embed=true`;

            const response = await fetch(thankYouUrl);
            if (response.ok) {
                const html = await response.text();

                // Check for redirect script in response
                const redirectMatch = html.match(/window\.top\.location\.href\s*=\s*['"]([^'"]+)['"]/);
                if (redirectMatch) {
                    window.location.href = redirectMatch[1];
                    return;
                }

                // Extract JSON from data-page-json attribute
                const jsonMatch = html.match(/data-page-json="([^"]+)"/);
                if (jsonMatch) {
                    const jsonStr = jsonMatch[1]
                        .replace(/&quot;/g, '"')
                        .replace(/&amp;/g, '&')
                        .replace(/&lt;/g, '<')
                        .replace(/&gt;/g, '>');
                    const pageData = JSON.parse(jsonStr) as IFormPageData;
                    this.renderThankYouPage(pageData);
                    return;
                }
            }
        } catch (error) {
            console.warn('[TruForm] Could not load thank you page, showing default:', error);
        }

        // Fallback: show simple thank you message
        this.renderDefaultThankYouPage();
    }

    private renderThankYouPage(pageData: IFormPageData): void {
        const containerEl = this.shadow.querySelector<HTMLElement>('.form-container');
        if (!containerEl) {
            return;
        }

        // Hide navigation menu
        const navMenu = this.shadow.querySelector('.package-navigation-menu');
        if (navMenu) {
            (navMenu as HTMLElement).style.display = 'none';
        }

        containerEl.innerHTML = '';
        this.formContainer = null;

        const thankYouWrapper = document.createElement('div');
        thankYouWrapper.classList.add('page', 'content', 'thank-you-page');

        // Render thank you page sections
        const fragment = this.serializer.renderFromJSON(pageData, {
            runtimeContext: this.formData?.runtimeContext,
            baseUrl: this.getApiBaseUrl()
        });

        thankYouWrapper.appendChild(fragment);
        containerEl.appendChild(thankYouWrapper);

        // Dispatch event
        this.dispatchEvent(new CustomEvent('tru-form-thank-you-shown', {
            detail: { formId: this.formData?.formId, category: this.category }
        }));
    }

    private renderDefaultThankYouPage(): void {
        const containerEl = this.shadow.querySelector<HTMLElement>('.form-container');
        if (!containerEl) {
            return;
        }

        // Hide navigation menu
        const navMenu = this.shadow.querySelector('.package-navigation-menu');
        if (navMenu) {
            (navMenu as HTMLElement).style.display = 'none';
        }

        containerEl.innerHTML = '';
        this.formContainer = null;

        // Create simple thank you message
        const thankYouWrapper = document.createElement('div');
        thankYouWrapper.classList.add('page', 'content', 'thank-you-page');
        thankYouWrapper.innerHTML = `
            <section data-section>
                <div class="mesh-container">
                    <div class="mesh-row">
                        <div class="col col-12">
                            <div class="element-content text-widget" style="text-align: center; padding: 40px 20px;">
                                <h2>Thank you!</h2>
                                <p>Your response has been submitted successfully.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        `;

        containerEl.appendChild(thankYouWrapper);

        // Dispatch event
        this.dispatchEvent(new CustomEvent('tru-form-thank-you-shown', {
            detail: { formId: this.formData?.formId, category: this.category }
        }));
    }

    private appendSystemFields(formData: globalThis.FormData): void {
        // __responderUrl - URL of the page where form is embedded
        if (!formData.has('__responderUrl')) {
            formData.append('__responderUrl', window.location.href);
        }

        // __responderTitle - title of the page where form is embedded
        if (!formData.has('__responderTitle')) {
            formData.append('__responderTitle', document.title || '');
        }

        const gdprEnabled = this.getAttribute('gdpr-enabled');
        if (gdprEnabled === 'true' || gdprEnabled === '') {
            formData.append('__isGdprEnabled', 'true');
            const privacyPolicyUrl = this.getAttribute('privacy-policy-url') || '';
            formData.append('__gdprPrivacyPolicyUrl', privacyPolicyUrl);
        }

        if (this.additional && !formData.has('__additional')) {
            formData.append('__additional', this.additional);
        }
    }

    private applyStructuredFieldValuesToFormData(formData: globalThis.FormData, fields: FormFieldValue[]): void {
        fields.forEach(field => {
            if (!field.fieldId || field.value === undefined || field.value === null) {
                return;
            }

            if (field.fieldType === 'file' || field.fieldType === 'signature') {
                return;
            }

            if (Array.isArray(field.value)) {
                formData.delete(field.fieldId);
                field.value.forEach(value => formData.append(field.fieldId, value));
                return;
            }

            formData.set(field.fieldId, field.value);
        });
    }

    private setLoadingState(loading: boolean): void {
        if (!this.formContainer) {
            return;
        }

        const inputs = this.formContainer.querySelectorAll('input, textarea, select');
        const buttons = this.formContainer.querySelectorAll('button');

        inputs.forEach(input => {
            (input as HTMLInputElement).readOnly = loading;
        });

        buttons.forEach(button => {
            (button as HTMLButtonElement).disabled = loading;
        });
    }

    private collectFieldValues(): FormFieldValue[] {
        if (!this.formContainer) {
            return [];
        }

        const fields: FormFieldValue[] = [];
        const processedFieldIds = new Set<string>();

        // Get all field elements
        const fieldElements = this.formContainer.querySelectorAll<HTMLElement>('[data-element="field"]');

        fieldElements.forEach(fieldEl => {
            const fieldId = fieldEl.id;
            if (!fieldId || processedFieldIds.has(fieldId)) {
                return;
            }
            processedFieldIds.add(fieldId);

            // Get field JSON data from data-element-json attribute
            const jsonAttr = fieldEl.getAttribute('data-element-json');
            let fieldJsonData: FieldJsonData | null = null;

            if (jsonAttr) {
                try {
                    fieldJsonData = JSON.parse(jsonAttr) as FieldJsonData;
                } catch (e) {
                    console.warn('[TruForm] Failed to parse field JSON:', e);
                }
            }

            const fieldType = fieldEl.dataset['field'] || fieldJsonData?.type || 'unknown';
            const label = fieldJsonData?.label || '';

            // Get field value based on type
            let value: any = null;

            switch (fieldType) {
                case 'checkbox': {
                    const checkboxes = fieldEl.querySelectorAll<HTMLInputElement>('input[type="checkbox"]:checked');
                    value = Array.from(checkboxes).map(cb => cb.value);
                    break;
                }
                case 'radio': {
                    const selected = fieldEl.querySelector<HTMLInputElement>('input[type="radio"]:checked');
                    value = selected?.value || null;
                    break;
                }
                case 'file': {
                    const fileInput = fieldEl.querySelector<HTMLInputElement>('input[type="file"]');
                    if (fileInput?.files && fileInput.files.length > 0) {
                        value = Array.from(fileInput.files).map(f => ({
                            name: f.name,
                            size: f.size,
                            type: f.type
                        }));
                    }
                    break;
                }
                default: {
                    const input = fieldEl.querySelector<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(
                        'input:not([type="hidden"]):not([type="checkbox"]):not([type="radio"]), textarea, select'
                    );
                    value = input?.value || null;
                }
            }

            // Build field value object
            const fieldValue: FormFieldValue = {
                fieldId,
                fieldType,
                label,
                value,
                crmMapping: fieldJsonData?.crmFieldData || null
            };

            fields.push(fieldValue);
        });

        return fields;
    }

    public async loadStyles(urls: string | string[]): Promise<void> {
        const urlArray = Array.isArray(urls) ? urls : [urls];
        try {
            // Remove base styles that might conflict with external CSS
            this.clearBaseStyles();
            await this.loadExternalStyles(urlArray);
        } catch (error) {
            console.error('[TruForm] Failed to load styles:', error);
        }
    }

    private clearBaseStyles(): void {
        // Replace base styles with minimal host styles to avoid conflicts
        const existingStyle = this.shadow.querySelector('style');
        if (existingStyle) {
            existingStyle.textContent = this.getMinimalHostStyles();
        }
    }

    // Private methods

    private async loadStylesIfNeeded(): Promise<void> {
        const stylesUrl = this.getAttribute('styles-url');
        if (stylesUrl) {
            // Support comma-separated URLs
            const urls = stylesUrl.split(',').map(u => u.trim()).filter(u => u);
            if (urls.length > 0) {
                await this.loadStyles(urls);
            }
        }
    }

    private render(): void {
        const formContainer = this.shadow.querySelector('.form-container');
        if (formContainer) {
            return;
        }

        // Always use minimal host styles initially
        // Full styles will be loaded from server in loadDefaultStyles()
        // or from styles-url attribute in loadStylesIfNeeded()
        // This prevents built-in styles from conflicting with server styles
        const styles = this.getMinimalHostStyles();

        // Mimic iframe structure: main > main-background > form-wrapper > form-container
        // CSS selectors are transformed to remove "body " prefix
        // Add "embedded" class to match iframe embedded mode (transparent background)
        this.shadow.innerHTML = `
            <style>${styles}</style>
            <main id="main" data-section-area="" class="embedded">
                <div class="main-background">
                    <div class="form-wrapper">
                        <div class="form-container"></div>
                    </div>
                </div>
            </main>
        `;
    }

    private getMinimalHostStyles(): string {
        return `
            :host {
                display: block;
                width: 100%;
            }
            *, *::before, *::after {
                box-sizing: border-box;
            }
            /* Override iframe-specific styles that break web component layout */
            .main-background {
                min-height: unset !important;
                width: 100% !important;
            }
            /* Constrain form-wrapper and form-container to the host width.
               Firefox ignores -webkit-fill-available on form-container's max-width,
               so without this override it stays at its fixed width and clips. */
            .form-wrapper, .form-container {
                min-width: 0 !important;
                max-width: 100% !important;
            }
            /* Reset browser default margin on form element */
            form {
                margin: 0;
            }
        `;
    }

    private async renderForm(): Promise<void> {
        const hasPageData = this.formData?.pageData;
        const hasPages = this.formData?.pages && this.formData.pages.length > 0;

        if (!hasPageData && !hasPages) {
            console.warn('[TruForm] No form data to render');
            return;
        }

        const containerEl = this.shadow.querySelector<HTMLElement>('.form-container');
        if (!containerEl) {
            return;
        }

        // Dispatch render start event
        this.dispatchEvent(new CustomEvent('tru-form-render-start', {
            detail: { formId: this.formData?.formId, category: this.category }
        }));

        // Load styles FIRST before rendering form content
        await this.loadDefaultStyles();
        this.connectUsedGoogleFonts();

        const pageStyles = this.formData?.pageData?.styles;
        if (pageStyles) {
            const cssVarsBlock = formStylesDataToCssVariables(pageStyles);
            if (cssVarsBlock) {
                const varsStyle = document.createElement('style');
                varsStyle.setAttribute('data-styles-data', '');
                varsStyle.textContent = cssVarsBlock;
                this.shadow.appendChild(varsStyle);
            }
        }

        this.clearNavigationMenu();

        // Clear previous content
        containerEl.innerHTML = '';
        this.pageElements = [];
        this.currentPageIndex = 0;

        // Create form element - direct child of form-container (same as iframe)
        this.formContainer = document.createElement('form');
        this.formContainer.setAttribute('novalidate', '');
        this.formContainer.classList.add('page', 'content');

        // Get variables for placeholder replacement
        const variables = this.getVariables();

        const pages = this.formData?.pages;
        const isMultiPages = pages && pages.length > 1;
        const navigationMenu = isMultiPages ? this.createNavigationMenu(pages) : null;
        this.observeFormWidthChanges();

        if (isMultiPages) {
            pages.forEach((page, index) => {
                const pageWrapper = document.createElement('div');
                pageWrapper.dataset['page'] = '';
                pageWrapper.dataset['pageId'] = page.id;
                pageWrapper.dataset['pageIndex'] = String(index);
                pageWrapper.style.display = index === 0 ? '' : 'none';

                if (page.data) {
                    const fragment = this.serializer.renderFromJSON(page.data, {
                        runtimeContext: this.formData!.runtimeContext,
                        baseUrl: this.getApiBaseUrl(),
                        variables: Object.keys(variables).length > 0 ? variables : undefined
                    });
                    pageWrapper.appendChild(fragment);
                } else {
                    console.warn('[TruForm] Page', index, 'has no data');
                }

                this.formContainer!.appendChild(pageWrapper);
                this.pageElements.push(pageWrapper);
            });
        } else if (hasPageData) {
            const fragment = this.serializer.renderFromJSON(this.formData!.pageData, {
                runtimeContext: this.formData?.runtimeContext,
                baseUrl: this.getApiBaseUrl(),
                variables: Object.keys(variables).length > 0 ? variables : undefined
            });
            this.formContainer.appendChild(fragment);
        } else if (pages && pages.length === 1 && pages[0].data) {
            const fragment = this.serializer.renderFromJSON(pages[0].data, {
                runtimeContext: this.formData?.runtimeContext,
                baseUrl: this.getApiBaseUrl(),
                variables: Object.keys(variables).length > 0 ? variables : undefined
            });
            this.formContainer.appendChild(fragment);
        }

        containerEl.appendChild(this.formContainer);

        if (navigationMenu) {
            this.placeNavigationMenu(navigationMenu, containerEl);
        }

        this.appendCustomCode(containerEl);

        // Show container now that styles are loaded and form is rendered
        containerEl.style.visibility = 'inherit';

        // Attach submit handler
        this.formContainer.addEventListener('submit', this.handleSubmit.bind(this));

        // Attach blur handlers for real-time validation
        this.attachFieldValidationListeners();

        // Attach navigation button handlers
        this.attachNavigationListeners();

        // Initialize date fields with input mask
        this.initializeDateFields();

        // Initialize dropdown fields
        this.initializeDropdownFields();

        // Initialize signature fields
        this.initializeSignatureFields();

        // Initialize file fields
        this.initializeFileFields();

        // Password toggle buttons are initialized directly in PasswordFieldRenderer

        // Initialize password strength validation
        this.initializePasswordStrengthValidation();

        // Initialize special widgets
        this.initializeSpecialWidgets();

        // Ensure GDPR checkbox if enabled
        this.ensureGdprField();

        this.initializeReCaptchaV2Checkbox();

        // Apply hidden field value overrides from the hidden-fields attribute
        this.applyHiddenFieldOverrides();

        // Dispatch ready event - form is fully rendered and interactive
        // Include fields metadata for pre-filling
        const fields = this.getFieldsMetadata();
        this.dispatchEvent(new CustomEvent('tru-form-ready', {
            detail: {
                formId: this.formData?.formId,
                category: this.category,
                pageCount: this.pageElements.length || 1,
                currentPage: this.currentPageIndex,
                fields
            }
        }));
    }

    private connectUsedGoogleFonts(): void {
        const fontsSet = new Set<string>();

        const existingGoogleFontsLink = document.getElementById('tspb-google-fonts');
        if (existingGoogleFontsLink) {
            const existingHref = existingGoogleFontsLink.getAttribute('href');
            if (existingHref) {
                const fonts = this.extractFontsFromGoogleLink(existingHref);
                fonts.forEach(font => fontsSet.add(font));
            }
        }

        if (this.formData?.optimizedGoogleFontsLink) {
            const newFonts = this.extractFontsFromGoogleLink(this.formData?.optimizedGoogleFontsLink);
            newFonts.forEach(font => fontsSet.add(font));
        }

        if (fontsSet.size > 0) {
            const usedFontsLink = this.buildGoogleFontsLink(fontsSet);

            if (existingGoogleFontsLink) {
                const existingPreload = document.getElementById('tspb-google-fonts-preload');
                existingPreload?.setAttribute('href', usedFontsLink);
                existingGoogleFontsLink.setAttribute('href', usedFontsLink);
            } else {
                const preloadLink = document.createElement('link');
                preloadLink.id = 'tspb-google-fonts-preload';
                preloadLink.rel = 'preload';
                preloadLink.as = 'style';
                preloadLink.href = usedFontsLink;

                const stylesheetLink = document.createElement('link');
                stylesheetLink.id = 'tspb-google-fonts';
                stylesheetLink.rel = 'stylesheet';
                stylesheetLink.href = usedFontsLink;

                document.head.appendChild(preloadLink);
                document.head.appendChild(stylesheetLink);
            }
        }
    }

    private extractFontsFromGoogleLink(url: string): string[] {
        try {
            const urlObj = new URL(url);
            const familyParam = urlObj.searchParams.get('family');
            if (!familyParam) {
                return [];
            }

            return familyParam.split('|');
        } catch {
            return [];
        }
    }

    private buildGoogleFontsLink(fontsSet: Set<string>): string {
        const fontsArray = Array.from(fontsSet);
        return `https://fonts.googleapis.com/css?family=${fontsArray.join('|')}&display=swap`;
    }

    private async handleSubmit(event: Event): Promise<void> {
        event.preventDefault();
        await this.submit();
    }

    private attachFieldValidationListeners(): void {
        if (!this.formContainer) {
            return;
        }

        // Add blur listeners to all inputs for real-time validation
        const inputs = this.formContainer.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(
            'input:not([type="hidden"]), textarea, select'
        );

        inputs.forEach(input => {
            const field = input.closest<HTMLElement>('[data-element="field"]');
            if (!field) {
                return;
            }
            const fieldType = field.dataset['field'];

            if (fieldType !== 'dropdown') {
                // Clear error on focus
                input.addEventListener('focus', () => {
                    this.validator.clearErrors([input]);
                });
            }

            // Validate on blur
            input.addEventListener('blur', () => {
                const errors = this.validator.validateField(field);
                if (errors.length > 0) {
                    this.validator.showErrors(errors);
                }
            });

            if (input instanceof HTMLInputElement) {
                // Clear error on checkbox change (for GDPR and regular checkboxes/radios)
                if (input.type === 'checkbox' || input.type === 'radio') {
                    input.addEventListener('change', () => {
                        this.validator.clearErrors([input]);
                        // Also clear error from the options container if it exists
                        const optionsContainer = input.closest('.options');
                        if (optionsContainer instanceof HTMLElement) {
                            this.validator.clearErrors([optionsContainer]);

                            const inputs = optionsContainer?.querySelectorAll<HTMLInputElement>('.input');
                            inputs?.forEach(input => {
                                input.classList.remove('min-error', 'max-error');
                            });
                        }
                    });
                }

                // Clear error on dropdown change
                if (fieldType === 'dropdown') {
                    input.addEventListener('change', () => {
                        this.validator.clearErrors([input]);
                        const errors = this.validator.validateField(field);
                        if (errors.length > 0) {
                            this.validator.showErrors(errors);
                        }
                    });
                }
            }
        });

        // Add listeners for TruCaptcha to clear errors on input
        const captchaInput = this.formContainer.querySelector<HTMLInputElement>('.tru-captcha-control');
        if (captchaInput) {
            captchaInput.addEventListener('input', () => {
                this.validator.clearErrors([captchaInput]);
            });
            captchaInput.addEventListener('focus', () => {
                this.validator.clearErrors([captchaInput]);
            });
        }

        // Add listener for Google reCAPTCHA callback to clear errors
        const recaptchaWidget = this.formContainer.querySelector<HTMLElement>('[data-widget="captcha"][data-captcha-type="googleReCaptcha"]');
        if (recaptchaWidget) {
            const wrapper = recaptchaWidget.querySelector<HTMLElement>('.recaptcha-wrapper');
            if (wrapper) {
                // Listen for recaptcha-callback event dispatched when user completes captcha
                wrapper.addEventListener('recaptcha-callback', () => {
                    this.validator.clearErrors([wrapper]);
                });
            }
        }
    }

    private attachNavigationListeners(): void {
        if (!this.formContainer) {
            return;
        }

        const previousButtons = this.formContainer.querySelectorAll<HTMLButtonElement>('.button-element--previous');
        previousButtons.forEach(btn => {
            btn.addEventListener('click', e => {
                e.preventDefault();
                this.goToPreviousPage();
            });
        });

        const nextButtons = this.formContainer.querySelectorAll<HTMLButtonElement>('.button-element--next');
        nextButtons.forEach(btn => {
            btn.addEventListener('click', e => {
                e.preventDefault();
                this.goToNextPage();
            });
        });
    }

    private dateFieldPreparers: DateFieldPreparer[] = [];
    private dropdownFieldPreparers: DropdownFieldPreparer[] = [];
    private signatureFieldPreparers: SignatureFieldPreparer[] = [];
    private fileFieldPreparers: FileFieldPreparer[] = [];

    private initializeDateFields(): void {
        if (!this.formContainer) {
            return;
        }

        this.dateFieldPreparers.forEach(p => p.destroy());
        this.dateFieldPreparers = [];

        this.dateFieldPreparers = initializeDateFields(this.formContainer);
    }

    private initializeDropdownFields(): void {
        if (!this.formContainer) {
            return;
        }

        this.dropdownFieldPreparers.forEach(p => p.destroy());
        this.dropdownFieldPreparers = [];

        this.dropdownFieldPreparers = initializeDropdownFields(this.formContainer);
    }

    private initializeSignatureFields(): void {
        if (!this.formContainer) {
            return;
        }

        this.signatureFieldPreparers.forEach(p => p.destroy());
        this.signatureFieldPreparers = [];

        const signaturePreparerOptions: SignatureFieldPreparerOptions = {
            onChange: field => {
                const placeholder = field.querySelector<HTMLElement>('.signature-placeholder');
                if (placeholder) {
                    this.validator.clearErrors([placeholder]);
                }
            },
            onBlur: field => {
                const errors = this.validator.validateField(field);
                if (errors.length > 0) {
                    this.validator.showErrors(errors);
                }
            }
        };

        this.signatureFieldPreparers = initializeSignatureFields(this.formContainer, signaturePreparerOptions);
    }

    private initializeFileFields(): void {
        if (!this.formContainer) {
            return;
        }

        this.fileFieldPreparers.forEach(p => p.destroy());
        this.fileFieldPreparers = [];

        const filePreparerOptions: FileFieldPreparerOptions = {
            onBlur: field => {
                const errors = this.validator.validateField(field);
                if (errors.length > 0) {
                    this.validator.showErrors(errors);
                }
            },
            onChange: field => {
                const fileSelectLabel = field.querySelector<HTMLElement>('.file-select-label');
                if (fileSelectLabel) {
                    this.validator.clearErrors([fileSelectLabel]);
                }
            },

        };

        this.fileFieldPreparers = initializeFileFields(this.formContainer, filePreparerOptions);
    }

    // Password toggles are now initialized directly in PasswordFieldRenderer

    private validatePasswordStrength(password: string): { length: boolean; case: boolean; number: boolean } {
        return {
            length: password.length >= 10,
            case: /[a-z]/.test(password) && /[A-Z]/.test(password),
            number: /\d/.test(password)
        };
    }

    private updatePasswordStrengthRules(rulesContainer: Element, password: string): void {
        const validation = this.validatePasswordStrength(password);

        const lengthRule = rulesContainer.querySelector<HTMLElement>('[data-rule="length"]');
        const caseRule = rulesContainer.querySelector<HTMLElement>('[data-rule="case"]');
        const numberRule = rulesContainer.querySelector<HTMLElement>('[data-rule="number"]');

        if (lengthRule) {
            lengthRule.classList.toggle('valid', validation.length);
        }
        if (caseRule) {
            caseRule.classList.toggle('valid', validation.case);
        }
        if (numberRule) {
            numberRule.classList.toggle('valid', validation.number);
        }
    }

    private initializePasswordStrengthValidation(): void {
        if (!this.formContainer) {
            return;
        }

        const strengthContainers = this.formContainer.querySelectorAll<HTMLElement>('.password-strength-rules');

        strengthContainers.forEach(container => {
            const fieldId = container.dataset['strengthFor'];
            if (!fieldId) {
                return;
            }

            const passwordInput = this.formContainer!.querySelector<HTMLInputElement>(`#field-${fieldId}`);
            if (!passwordInput) {
                return;
            }

            const showRules = () => {
                container.classList.remove('untouched');
            };
            passwordInput.addEventListener('focus', showRules, { once: true });
            passwordInput.addEventListener('input', showRules, { once: true });
            passwordInput.addEventListener('input', () => {
                this.updatePasswordStrengthRules(container, passwordInput.value);
            });

            if (passwordInput.value) {
                showRules();
                this.updatePasswordStrengthRules(container, passwordInput.value);
            }
        });
    }

    private initializeSpecialWidgets(): void {
        if (!this.formContainer) {
            return;
        }

        const detail = {
            formId: this.formData?.formId,
            category: this.category,
        };

        const widgets = [
            { selector: 'signin', event: 'tru-form-login-clicked' },
            { selector: 'signup', event: 'tru-form-sign-up-clicked' },
            { selector: 'forgotpassword', event: 'tru-form-forgot-password-clicked' },
        ];

        widgets.forEach(({ selector, event }) => {
            this.formContainer!
                .querySelectorAll<HTMLElement>(`[data-widget="${selector}"]`)
                .forEach(widget => {
                    widget.addEventListener('click', () => {
                        this.dispatchEvent(
                            new CustomEvent(event, { detail })
                        );
                    });
                });
        });
    }

    private ensureGdprField(): void {
        const gdprEnabled = this.getAttribute('gdpr-enabled');
        if (gdprEnabled !== 'true' && gdprEnabled !== '') {
            return;
        }

        if (!this.formContainer) {
            return;
        }

        // Check if GDPR field already exists
        const existingGdpr = this.formContainer.querySelector('[data-field="gdprPolicyConsent"]');
        if (existingGdpr) {
            return;
        }

        let searchContainer: HTMLElement = this.formContainer;
        if (this.pageElements.length > 1) {
            const lastPage = this.pageElements[this.pageElements.length - 1];
            if (lastPage) {
                searchContainer = lastPage;
            }
        }

        // Find the submit widget row
        const submitRow = searchContainer.querySelector<HTMLElement>('[data-element="widget"][data-widget="submit"]')
            ?.closest<HTMLElement>('[data-mesh-row]');
        if (!submitRow) {
            return;
        }

        // Get submit button text for description
        const submitButtonText = searchContainer.querySelector('.button-element--submit .button-label')?.textContent || 'Submit';

        // Get privacy policy URL
        const privacyPolicyUrl = this.getAttribute('privacy-policy-url') || '';

        // Create GDPR field
        const gdprField = this.createGdprField(privacyPolicyUrl, submitButtonText);

        // Insert before submit row
        submitRow.parentNode?.insertBefore(gdprField, submitRow);
    }

    private createGdprField(privacyPolicyUrl: string, submitButtonText: string): HTMLElement {
        const gdprId = `gdpr-${Date.now()}`;

        // Create the row container
        const row = document.createElement('div');
        row.classList.add('row');
        row.setAttribute('data-mesh-row', '');

        const col = document.createElement('div');
        col.classList.add('col', 'col-12');

        // Create field wrapper
        const fieldWrapper = document.createElement('div');
        fieldWrapper.classList.add('element-wrapper');
        fieldWrapper.setAttribute('data-element', 'field');
        fieldWrapper.setAttribute('data-field', 'gdprPolicyConsent');
        fieldWrapper.id = gdprId;

        const elementContent = document.createElement('div');
        elementContent.classList.add('element-content');

        // Create fieldset
        const fieldset = document.createElement('fieldset');
        fieldset.classList.add('field', 'checkbox', 'required');

        // Create options container
        const options = document.createElement('div');
        options.classList.add('options');

        const option = document.createElement('div');
        option.classList.add('option');

        // Create checkbox input
        const input = document.createElement('input');
        input.id = `${gdprId}-input`;
        input.classList.add('input');
        input.type = 'checkbox';
        input.name = '__gdprPolicyConsent';
        input.required = true;
        input.value = TranslateUtils.GDPR.AGREEMENT_DEFAULT_VOLUME;
        input.setAttribute('data-label', TranslateUtils.GDPR.AGREEMENT_DEFAULT_VOLUME);

        // Create label
        const label = document.createElement('label');
        label.classList.add('label');
        label.setAttribute('for', `${gdprId}-input`);

        const labelSpan = document.createElement('span');
        if (privacyPolicyUrl) {
            labelSpan.innerHTML = `${TranslateUtils.GDPR.I_ACCEPT}<a href="${privacyPolicyUrl}" target="_blank" rel="noopener noreferrer">${TranslateUtils.GDPR.PRIVACY_POLICY}</a>`;
        } else {
            labelSpan.innerHTML = `${TranslateUtils.GDPR.I_ACCEPT}<span>${TranslateUtils.GDPR.PRIVACY_POLICY}</span>`;
        }
        label.appendChild(labelSpan);

        option.appendChild(input);
        option.appendChild(label);
        options.appendChild(option);

        // Create description
        const description = document.createElement('span');
        description.classList.add('description');
        description.textContent = `${TranslateUtils.GDPR.BY_CLICKING_THE}${submitButtonText}${TranslateUtils.GDPR.YOU_GIVE_US_PERMISSION}`;

        fieldset.appendChild(options);
        fieldset.appendChild(description);
        elementContent.appendChild(fieldset);
        fieldWrapper.appendChild(elementContent);
        col.appendChild(fieldWrapper);
        row.appendChild(col);

        return row;
    }

    private scrollToActiveStep(): void {
        requestAnimationFrame(() => {
            const viewport = this.shadow.querySelector<HTMLDivElement>('.package-navigation-menu .wizard-steps-viewport');
            if (!viewport) {
                return;
            }

            const active = viewport.querySelector<HTMLElement>('.wizard-step.active');
            if (!active) {
                return;
            }

            try {
                active.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
            } catch {
                const rect = active.getBoundingClientRect();
                viewport.scrollLeft += rect.left - viewport.getBoundingClientRect().left - viewport.clientWidth + rect.width;
            }
        });
    }

    public goToNextPage(): boolean {
        if (this.pageElements.length === 0) {
            return false;
        }
        if (this.currentPageIndex >= this.pageElements.length - 1) {
            return false;
        }

        // Hide current page, show next
        this.pageElements[this.currentPageIndex].style.display = 'none';
        this.currentPageIndex++;
        this.pageElements[this.currentPageIndex].style.display = '';

        this.updateNavigationMenuState();

        this.dispatchEvent(new CustomEvent('tru-form-page-changed', {
            detail: { formId: this.formData?.formId, category: this.category, pageIndex: this.currentPageIndex, totalPages: this.pageElements.length }
        }));

        this.scrollToActiveStep();

        return true;
    }

    public goToPreviousPage(): boolean {
        if (this.pageElements.length === 0) {
            return false;
        }
        if (this.currentPageIndex <= 0) {
            return false;
        }

        // Hide current page, show previous
        this.pageElements[this.currentPageIndex].style.display = 'none';
        this.currentPageIndex--;
        this.pageElements[this.currentPageIndex].style.display = '';

        this.updateNavigationMenuState();

        this.dispatchEvent(new CustomEvent('tru-form-page-changed', {
            detail: { formId: this.formData?.formId, category: this.category, pageIndex: this.currentPageIndex, totalPages: this.pageElements.length }
        }));

        this.scrollToActiveStep();

        return true;
    }

    public goToPage(pageIndex: number): boolean {
        if (this.pageElements.length === 0) {
            return false;
        }
        if (pageIndex < 0 || pageIndex >= this.pageElements.length) {
            return false;
        }
        if (pageIndex === this.currentPageIndex) {
            return true;
        }

        // Navigate to target page
        this.pageElements[this.currentPageIndex].style.display = 'none';
        this.currentPageIndex = pageIndex;
        this.pageElements[this.currentPageIndex].style.display = '';

        this.updateNavigationMenuState();

        this.dispatchEvent(new CustomEvent('tru-form-page-changed', {
            detail: { formId: this.formData?.formId, category: this.category, pageIndex: this.currentPageIndex, totalPages: this.pageElements.length }
        }));

        this.scrollToActiveStep();

        return true;
    }

    public get currentPage(): number {
        return this.currentPageIndex;
    }

    public get totalPages(): number {
        return this.pageElements.length || 1;
    }

    private isLastPage(): boolean {
        return this.pageElements.length === 0 || this.currentPageIndex >= this.pageElements.length - 1;
    }

    private clearNavigationMenu(): void {
        const formWrapper = this.shadow.querySelector('.form-wrapper');
        if (!formWrapper) {
            return;
        }

        // Cleanup resize observer
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
            this.resizeObserver = null;
        }

        formWrapper.querySelector('.package-navigation-menu')?.remove();

        const layoutEl = formWrapper.querySelector('.form-navigation-layout');
        const containerEl = formWrapper.querySelector('.form-container');
        if (layoutEl && containerEl && layoutEl.contains(containerEl)) {
            formWrapper.insertBefore(containerEl, layoutEl);
            layoutEl.remove();
        }
    }

    private getNavigationType(): FormNavigationType {
        return this.formData?.settings?.navigationType ?? 'Tabs';
    }

    private getNavigationPosition(): FormNavigationPosition {
        return this.formData?.settings?.navigationPosition ?? 'Top';
    }

    private createNavigationMenu(pages: { id: string; name?: string }[]): HTMLElement | null {
        const navigationType = this.getNavigationType();
        if (navigationType === 'OnlyButtons') {
            return null;
        }

        if (navigationType === 'ProgressBar') {
            return this.createProgressBarNavigationMenu(pages);
        }

        return this.createTabsNavigationMenu(pages);
    }

    private createTabsNavigationMenu(pages: { id: string; name?: string }[]): HTMLElement {
        const navMenu = document.createElement('div');
        navMenu.classList.add('package-navigation-menu');
        navMenu.setAttribute('data-layout', 'tabbed');

        // Create pages list
        const pagesList = document.createElement('ul');
        pagesList.classList.add('pages-list');

        pages.forEach((page, index) => {
            const pageItem = document.createElement('li');
            pageItem.classList.add('page-item');
            pageItem.setAttribute('data-page-id', page.id);
            pageItem.setAttribute('data-nav-index', String(index));
            pageItem.tabIndex = 0;

            // Mark first page as active
            if (index === 0) {
                pageItem.classList.add('active');
            }

            const nameSpan = document.createElement('span');
            nameSpan.classList.add('name');
            nameSpan.textContent = page.name || `Page ${index + 1}`;
            pageItem.appendChild(nameSpan);

            // Click handler
            pageItem.onclick = () => this.navigateToPageByNav(index);
            pageItem.onkeydown = e => {
                if (e.key === 'Enter') {
                    this.navigateToPageByNav(index);
                }
            };

            pagesList.appendChild(pageItem);
        });
        navMenu.appendChild(pagesList);

        // Add left/right controls for scrollable tabs
        this.createLeftRightControls(pagesList, navMenu);

        return navMenu;
    }

    private createLeftRightControls(pagesList: HTMLUListElement, packageNavigationMenu: HTMLDivElement): void {
        const leftControl = this.createLeftControl(pagesList);
        packageNavigationMenu.insertBefore(leftControl, pagesList);

        const rightControl = this.createRightControl(pagesList);
        packageNavigationMenu.appendChild(rightControl);

        // Observe form wrapper resize to handle control visibility
        const formWrapper = this.shadow.querySelector<HTMLElement>('.form-wrapper');
        if (formWrapper) {
            this.resizeObserver = new ResizeObserver(() => {
                this.handleFormWrapperResize(pagesList, formWrapper);
            });
            this.resizeObserver.observe(formWrapper);
        }
    }

    private createLeftControl(pagesList: HTMLUListElement): HTMLDivElement {
        const leftControl = document.createElement('div');
        leftControl.classList.add('left-control');
        leftControl.tabIndex = 0;

        const iconLeftChevron = document.createElement('span');
        iconLeftChevron.classList.add('icon', 'icon-left-chevron');
        if (pagesList.firstElementChild?.classList.contains('active')) {
            leftControl.classList.add('disabled');
        }
        leftControl.append(iconLeftChevron);

        leftControl.onclick = () => {
            const currentPageElement = pagesList.querySelector(`[data-page-id="${this.getCurrentPageId()}"]`);
            const previousElement = currentPageElement?.previousElementSibling;
            if (previousElement) {
                const pageIndex = parseInt(previousElement.getAttribute('data-nav-index') || '0', 10);
                this.goToPage(pageIndex);
            }
        };

        leftControl.onkeydown = event => {
            if (event.key === 'Enter') {
                const currentPageElement = pagesList.querySelector(`[data-page-id="${this.getCurrentPageId()}"]`);
                const previousElement = currentPageElement?.previousElementSibling;
                if (previousElement) {
                    const pageIndex = parseInt(previousElement.getAttribute('data-nav-index') || '0', 10);
                    this.goToPage(pageIndex);
                }
            }
        };

        return leftControl;
    }

    private createRightControl(pagesList: HTMLUListElement): HTMLDivElement {
        const rightControl = document.createElement('div');
        rightControl.classList.add('right-control');
        rightControl.tabIndex = 0;

        const iconRightChevron = document.createElement('span');
        iconRightChevron.classList.add('icon', 'icon-right-chevron');
        if (pagesList.lastElementChild?.classList.contains('active')) {
            rightControl.classList.add('disabled');
        }
        rightControl.append(iconRightChevron);

        rightControl.onclick = () => {
            const currentPageElement = pagesList.querySelector(`[data-page-id="${this.getCurrentPageId()}"]`);
            const nextElement = currentPageElement?.nextElementSibling;
            if (nextElement) {
                const pageIndex = parseInt(nextElement.getAttribute('data-nav-index') || '0', 10);
                this.goToPage(pageIndex);
            }
        };

        rightControl.onkeydown = event => {
            if (event.key === 'Enter') {
                const currentPageElement = pagesList.querySelector(`[data-page-id="${this.getCurrentPageId()}"]`);
                const nextElement = currentPageElement?.nextElementSibling;
                if (nextElement) {
                    const pageIndex = parseInt(nextElement.getAttribute('data-nav-index') || '0', 10);
                    this.goToPage(pageIndex);
                }
            }
        };

        return rightControl;
    }

    private getCurrentPageId(): string {
        const pages = this.formData?.pages || [];
        if (this.currentPageIndex >= 0 && this.currentPageIndex < pages.length) {
            return pages[this.currentPageIndex].id;
        }
        return pages[0]?.id || '';
    }

    private handleFormWrapperResize(pagesList: HTMLUListElement, formWrapper: HTMLElement): void {
        // Simple check: if pages list is wider than form wrapper, show controls
        const pagesListWidth = pagesList.scrollWidth;
        const formWrapperWidth = formWrapper.offsetWidth;

        const leftControl = formWrapper.querySelector<HTMLElement>('.left-control');
        const rightControl = formWrapper.querySelector<HTMLElement>('.right-control');

        if (pagesListWidth > formWrapperWidth) {
            leftControl?.classList.remove('hidden');
            rightControl?.classList.remove('hidden');
        } else {
            leftControl?.classList.add('hidden');
            rightControl?.classList.add('hidden');
        }
    }

    private createProgressBarNavigationMenu(pages: { id: string; name?: string }[]): HTMLElement {
        const position = this.getNavigationPosition();
        const navMenu = document.createElement('div');
        navMenu.classList.add('package-navigation-menu', 'wizard-steps');
        navMenu.setAttribute('data-navigation-position', position.toLowerCase());

        const stepsList = document.createElement('ul');
        stepsList.classList.add('wizard-steps-container');

        const isVertical = position === 'Left' || position === 'Right';
        const separatorIconClass = isVertical
            ? 'icon icon-down-chevron'
            : 'icon icon-right-chevron';

        pages.forEach((page, index) => {
            const stepItem = document.createElement('li');

            const stepButton = document.createElement('button');
            stepButton.classList.add('wizard-step');
            stepButton.setAttribute('data-page-id', page.id);
            stepButton.setAttribute('data-nav-index', String(index));
            stepButton.type = 'button';

            if (index === 0) {
                stepButton.classList.add('active');
            } else {
                stepButton.classList.add('incomplete');
            }

            const indicator = document.createElement('div');
            indicator.classList.add('wizard-step-indicator');
            indicator.textContent = String(index + 1);
            stepButton.appendChild(indicator);

            stepButton.onclick = () => {
                this.navigateToPageByNav(index);
            };

            stepButton.onmousedown = event => {
                event.preventDefault();
            };

            stepItem.appendChild(stepButton);

            if (index < pages.length - 1) {
                const separator = document.createElement('div');
                separator.classList.add('wizard-step-separator');
                const separatorIcon = document.createElement('span');
                separatorIcon.className = separatorIconClass;
                separator.appendChild(separatorIcon);
                stepItem.appendChild(separator);
            }

            stepsList.appendChild(stepItem);
        });

        if (isVertical) {
            navMenu.appendChild(stepsList);
            return navMenu;
        }

        const viewport = document.createElement('div');
        viewport.classList.add('wizard-steps-viewport');
        viewport.style.scrollbarWidth = 'none';
        viewport.appendChild(stepsList);

        viewport.addEventListener('scroll', () => {
            this.checkScrollbarState();
        }, { passive: true });

        const leftControl = this.createScrollControl('left', viewport);
        const rightControl = this.createScrollControl('right', viewport);

        navMenu.append(leftControl, viewport, rightControl);
        this.initializeViewportPosition(viewport, stepsList);

        requestAnimationFrame(() => {
            this.checkScrollbarState(true);
        });

        return navMenu;
    }

    private createScrollControl(direction: 'left' | 'right', viewport: HTMLElement): HTMLDivElement {
        const control = document.createElement('div');
        control.classList.add(direction === 'left' ? 'left-control' : 'right-control');
        control.tabIndex = 0;

        const icon = document.createElement('span');
        icon.classList.add('icon', direction === 'left' ? 'icon-left-chevron' : 'icon-right-chevron');
        control.append(icon);

        const scroll = () => {
            if (control.classList.contains('disabled')) {
                return;
            }

            viewport.scrollBy({
                left: direction === 'left' ? -viewport.clientWidth : viewport.clientWidth,
                behavior: 'smooth'
            });
        };

        control.onclick = scroll;
        control.onkeydown = event => {
            if (event.key === 'Enter') {
                scroll();
            }
        };

        return control;
    }

    private initializeViewportPosition(viewport: HTMLElement, stepsList: HTMLElement): void {
        const updateViewportPosition = () => {
            viewport.classList.toggle('content-centered', stepsList.scrollWidth <= viewport.clientWidth + 1);
            this.updateControlsVisibility();
            this.checkScrollbarState();
        };

        if (this.viewportResizeObserver) {
            this.viewportResizeObserver.disconnect();
        }

        this.viewportResizeObserver = new ResizeObserver(updateViewportPosition);
        this.viewportResizeObserver.observe(viewport);
        this.viewportResizeObserver.observe(stepsList);

        requestAnimationFrame(updateViewportPosition);
    }

    private checkScrollbarState(force = false): void {
        const viewport = this.shadow.querySelector<HTMLDivElement>('.package-navigation-menu .wizard-steps-viewport');
        if (!viewport) {
            return;
        }

        const newState = {
            canScrollLeft: viewport.scrollLeft > 0,
            canScrollRight: viewport.scrollLeft < (viewport.scrollWidth - viewport.clientWidth - 1),
            isAtStart: viewport.scrollLeft <= 0,
            isAtEnd: viewport.scrollLeft >= (viewport.scrollWidth - viewport.clientWidth - 1)
        };

        const stateChanged =
            this.scrollState.canScrollLeft !== newState.canScrollLeft ||
            this.scrollState.canScrollRight !== newState.canScrollRight ||
            this.scrollState.isAtStart !== newState.isAtStart ||
            this.scrollState.isAtEnd !== newState.isAtEnd;

        if (force || stateChanged) {
            this.scrollState = newState;
            this.onScrollStateChanged(newState);
        }
    }

    private updateControlsVisibility(): void {
        const containerWidth = this.getWizardStepsContainer();
        const formWrapper = this.shadow.querySelector<HTMLDivElement>('.form-wrapper');

        const leftControl = this.shadow.querySelector<HTMLDivElement>('.package-navigation-menu .left-control');
        const rightControl = this.shadow.querySelector<HTMLDivElement>('.package-navigation-menu .right-control');
        if (!leftControl || !rightControl) {
            return;
        }

        if (containerWidth === null || !formWrapper) {
            leftControl?.classList.remove('hidden');
            rightControl?.classList.remove('hidden');
            return;
        }

        if (containerWidth + 32 < Math.round(formWrapper.getBoundingClientRect().width)) {
            leftControl?.classList.add('hidden');
            rightControl?.classList.add('hidden');
            this.visibleSeparatorsIcon(true);
        } else {
            leftControl?.classList.remove('hidden');
            rightControl?.classList.remove('hidden');
            this.visibleSeparatorsIcon(false);
        }
    }

    private setControlState(control: HTMLElement | null, enabled: boolean): void {
        if (!control) {
            return;
        }

        control.classList.toggle('disabled', !enabled);
        control.toggleAttribute('aria-disabled', !enabled);
    }

    private onScrollStateChanged(state: typeof this.scrollState): void {
        const leftControl = this.shadow.querySelector<HTMLDivElement>('.package-navigation-menu .left-control');
        const rightControl = this.shadow.querySelector<HTMLDivElement>('.package-navigation-menu .right-control');

        this.setControlState(leftControl, state.canScrollLeft);
        this.setControlState(rightControl, state.canScrollRight);
    }

    private visibleSeparatorsIcon(visible: boolean): void {
        const element = this.shadow.querySelector<HTMLUListElement>('.wizard-steps-container');
        if (!element) {
            return;
        }

        const listItems = element.querySelectorAll('li');
        listItems.forEach(li => {
            const separator = li.querySelector<HTMLDivElement>('.wizard-step-separator');
            if (separator) {
                const icon = separator.querySelector('span');
                if (icon) {
                    if (visible) {
                        icon.style.visibility = 'visible';
                    } else {
                        icon.style.visibility = 'hidden';
                    }
                }
            }
        });
    }

    private getWizardStepsContainer(): number | null {
        const element = this.shadow.querySelector<HTMLUListElement>('.wizard-steps-container');
        if (element) {
            const listItems = element.querySelectorAll('li');
            let totalWidth = 0;

            listItems.forEach(li => {
                totalWidth += li.getBoundingClientRect().width;
            });

            return totalWidth;
        }

        return null;
    }

    private observeFormWidthChanges(): void {
        const formWrapper = this.shadow.querySelector<HTMLDivElement>('.form-wrapper');
        if (!formWrapper) {
            return;
        }

        if (this.formResizeObserver) {
            this.formResizeObserver.disconnect();
        }

        this.formResizeObserver = new ResizeObserver(entries => {
            for (const entry of entries) {
                const width = entry.contentRect?.width ?? (entry.target as HTMLElement).getBoundingClientRect().width;
                this.onFormWidthChanged(Math.round(width));
            }
        });

        this.formResizeObserver.observe(formWrapper);
    }

    private onFormWidthChanged(width: number): void {
        if (this.lastFormWidth === width) {
            return;
        }
        this.lastFormWidth = width;

        this.updateNavigationMenuState();

        const viewport = this.shadow.querySelector<HTMLDivElement>('.package-navigation-menu .wizard-steps-viewport');
        if (viewport) {
            const active = viewport.querySelector<HTMLElement>('.wizard-step.active');
            if (active) {
                try {
                    active.scrollIntoView({ block: 'nearest', inline: 'center' });
                } catch (e) {
                    const rect = active.getBoundingClientRect();
                    viewport.scrollLeft += rect.left - viewport.getBoundingClientRect().left - (viewport.clientWidth) + (rect.width);
                }
            }
        }
    }

    private placeNavigationMenu(navMenu: HTMLElement, containerEl: HTMLElement): void {
        const position = this.getNavigationType() === 'ProgressBar' ? this.getNavigationPosition() : 'Top';
        navMenu.setAttribute('data-navigation-position', position.toLowerCase());

        const formWrapper = this.shadow.querySelector('.form-wrapper');
        if (!formWrapper) {
            containerEl.parentElement?.insertBefore(navMenu, containerEl);
            return;
        }

        if (position === 'Left' || position === 'Right') {
            let layoutEl = formWrapper.querySelector('.form-navigation-layout') as HTMLElement | null;
            if (!layoutEl) {
                layoutEl = document.createElement('div');
                layoutEl.classList.add('form-navigation-layout');
                formWrapper.insertBefore(layoutEl, containerEl);
                layoutEl.appendChild(containerEl);
            }

            if (position === 'Left') {
                layoutEl.insertBefore(navMenu, containerEl);
            } else {
                layoutEl.appendChild(navMenu);
            }
            return;
        }

        if (position === 'Bottom') {
            formWrapper.appendChild(navMenu);
            return;
        }

        formWrapper.insertBefore(navMenu, containerEl);
    }

    private updateProgressBarStepIndicator(stepButton: HTMLButtonElement, index: number): void {
        const indicator = stepButton.querySelector('.wizard-step-indicator');
        if (!indicator) {
            return;
        }

        indicator.textContent = '';
        if (index < this.currentPageIndex) {
            const checkIcon = document.createElement('span');
            checkIcon.className = 'icon icon-check';
            indicator.appendChild(checkIcon);
        } else {
            indicator.textContent = String(index + 1);
        }
    }

    private navigateToPageByNav(pageIndex: number): void {
        this.goToPage(pageIndex);
    }

    private updateNavigationMenuState(): void {
        if (this.getNavigationType() === 'ProgressBar') {
            const stepButtons = this.shadow.querySelectorAll<HTMLButtonElement>('.package-navigation-menu .wizard-step');
            stepButtons.forEach((stepButton, index) => {
                stepButton.classList.remove('active', 'completed', 'incomplete');

                if (index < this.currentPageIndex) {
                    stepButton.classList.add('completed');
                } else if (index === this.currentPageIndex) {
                    stepButton.classList.add('active');
                } else {
                    stepButton.classList.add('incomplete');
                }

                this.updateProgressBarStepIndicator(stepButton, index);
            });
            return;
        }

        const navItems = this.shadow.querySelectorAll('.package-navigation-menu .page-item');
        navItems.forEach((item, index) => {
            if (index === this.currentPageIndex) {
                item.classList.add('active');
            } else {
                item.classList.remove('active');
            }
        });

        // Update left/right controls state
        this.updateNavigationControlsState();
    }

    private updateNavigationControlsState(): void {
        const pagesList = this.shadow.querySelector<HTMLUListElement>('.package-navigation-menu .pages-list');
        if (!pagesList) {
            return;
        }

        const leftControl = this.shadow.querySelector<HTMLElement>('.package-navigation-menu .left-control');
        const rightControl = this.shadow.querySelector<HTMLElement>('.package-navigation-menu .right-control');

        const firstPage = pagesList.firstElementChild;
        const lastPage = pagesList.lastElementChild;

        if (leftControl) {
            if (firstPage?.classList.contains('active')) {
                leftControl.classList.add('disabled');
            } else {
                leftControl.classList.remove('disabled');
            }
        }

        if (rightControl) {
            if (lastPage?.classList.contains('active')) {
                rightControl.classList.add('disabled');
            } else {
                rightControl.classList.remove('disabled');
            }
        }
    }

    private loadFormIfNeeded(): void {
        const formId = this.getAttribute('form-id');
        if (formId) {
            this.loadForm(formId);
        }
    }

    private getApiBaseUrl(): string {
        const apiUrl = this.getAttribute('api-url');
        if (apiUrl) {
            return apiUrl;
        }

        const scripts = Array.from(document.querySelectorAll('script[src*="tru-forms-embed"]'));
        for (const script of scripts) {
            const src = script.getAttribute('src');
            if (src) {
                try {
                    if (src.startsWith('http://') || src.startsWith('https://')) {
                        const url = new URL(src);
                        return `${url.protocol}//${url.host}`;
                    }
                } catch {
                    // Ignore invalid URLs
                }
            }
        }

        console.warn('[TruForm] Could not detect API base URL from script src. Please specify api-url attribute on <tru-form> element.');
        return window.location.origin;
    }

    private async loadDefaultStyles(): Promise<void> {
        try {
            const baseUrl = this.getApiBaseUrl();

            this.loadFontsIntoDocument(baseUrl);

            const styleUrl = `${baseUrl}/static/styles/app.css`;
            const response = await fetch(styleUrl);
            if (!response.ok) {
                console.warn('[TruForm] Failed to load default styles:', response.status);
                return;
            }

            const cssText = await response.text();

            const styleElement = document.createElement('style');
            styleElement.setAttribute('data-default-styles', '');
            styleElement.textContent = cssText;
            this.shadow.appendChild(styleElement);
        } catch (error) {
            console.warn('[TruForm] Error loading default styles:', error);
        }
    }

    private async loadExternalStyles(urls: string[]): Promise<void> {
        const loadPromises = urls.map(url => this.loadExternalStylesheet(url));
        await Promise.all(loadPromises);
    }

    private async loadExternalStylesheet(url: string): Promise<void> {
        if (this.loadedStylesheets.has(url)) {
            return;
        }

        const response = await fetch(url);
        if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
        }

        const css = await response.text();

        const style = document.createElement('style');
        style.textContent = css;
        style.dataset['url'] = url;
        this.shadow.appendChild(style);

        this.loadedStylesheets.add(url);
    }

    private loadFontsIntoDocument(baseUrl: string): void {
        if (TruFormElement.fontsLoaded) {
            return;
        }

        const existingFontsLink = document.querySelector('link[data-tru-form-fonts]');
        if (existingFontsLink) {
            TruFormElement.fontsLoaded = true;
            return;
        }

        const fontsUrl = `${baseUrl}/static/styles/fonts.css`;

        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = fontsUrl;
        link.setAttribute('data-tru-form-fonts', '');
        link.crossOrigin = 'anonymous';

        document.head.appendChild(link);
        TruFormElement.fontsLoaded = true;
    }


    private displaySubmissionError(errorCode: string): void {
        if (!this.formContainer) {
            return;
        }

        this.clearSubmissionError();

        const submitWidgets = Array.from(
            this.formContainer.querySelectorAll<HTMLElement>('[data-element="widget"][data-widget="submit"]')
        );
        const submitWidget = [...submitWidgets].reverse().find(w => w.querySelector('button[type="submit"]'))
            ?? submitWidgets[submitWidgets.length - 1];

        const submitRow = submitWidget?.closest<HTMLElement>('[data-mesh-row]');
        if (!submitRow) {
            console.warn('[TruForm] Submit section not found for error display');
            return;
        }

        const submitPageIndex = this.pageElements.findIndex(page => page.contains(submitRow));
        if (submitPageIndex !== -1 && submitPageIndex !== this.currentPageIndex) {
            this.goToPage(submitPageIndex);
        }

        const errorKey = TranslateUtils.prepareKey(errorCode);
        const errorMessage = TranslateUtils.getTranslation('ERRORS', errorKey) || errorCode;

        const errorWidget = document.createElement('div');
        errorWidget.classList.add('element-wrapper');
        errorWidget.setAttribute('data-element', 'widget');
        errorWidget.setAttribute('data-widget', 'errors');

        const elementContent = document.createElement('div');
        elementContent.classList.add('element-content');

        const errorSpan = document.createElement('span');
        errorSpan.classList.add('error-message');
        errorSpan.textContent = errorMessage;

        elementContent.appendChild(errorSpan);
        errorWidget.appendChild(elementContent);

        const col = document.createElement('div');
        col.classList.add('col', 'col-12');
        col.appendChild(errorWidget);

        const row = document.createElement('div');
        row.classList.add('row');
        row.setAttribute('data-mesh-row', '');
        row.appendChild(col);

        submitRow.parentNode?.insertBefore(row, submitRow);

        errorWidget.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    private clearSubmissionError(): void {
        if (!this.formContainer) {
            return;
        }

        const existingErrors = this.formContainer.querySelectorAll('[data-widget="errors"]');
        existingErrors.forEach(error => error.closest('[data-mesh-row]')?.remove());
    }

    private displayLoadError(errorCode: string): void {
        if (!this.shadow) {
            return;
        }

        const formContainer = this.shadow.querySelector('.form-container');
        if (!formContainer) {
            return;
        }

        const errorKey = TranslateUtils.prepareKey(errorCode);
        const errorMessage = TranslateUtils.getTranslation('ERRORS', errorKey) || errorCode;

        const errorWidget = document.createElement('div');
        errorWidget.classList.add('element-wrapper');
        errorWidget.setAttribute('data-element', 'widget');
        errorWidget.setAttribute('data-widget', 'errors');

        const elementContent = document.createElement('div');
        elementContent.classList.add('element-content');

        const errorTitle = document.createElement('div');
        errorTitle.classList.add('error-message');
        errorTitle.textContent = TranslateUtils.ERRORS.FORM_NOT_AVAILABLE;

        const errorDetail = document.createElement('div');
        errorDetail.classList.add('error-message');
        errorDetail.textContent = errorMessage;

        elementContent.appendChild(errorTitle);
        elementContent.appendChild(errorDetail);
        errorWidget.appendChild(elementContent);

        const col = document.createElement('div');
        col.classList.add('col', 'col-12');
        col.appendChild(errorWidget);

        const row = document.createElement('div');
        row.classList.add('row');
        row.setAttribute('data-mesh-row', '');
        row.appendChild(col);

        formContainer.appendChild(row);
    }

    private appendCustomCode(target: HTMLElement): void {
        const customCodeHtml = this.formData?.settings?.customCodeHtml;
        if (!customCodeHtml) {
            return;
        }

        const template = document.createElement('template');
        template.innerHTML = customCodeHtml;

        target.appendChild(template.content.cloneNode(true));

        this.activateScripts(target);
    }

    private activateScripts(root: ParentNode): void {
        const scripts = root.querySelectorAll('script');

        scripts.forEach(oldScript => {
            const newScript = document.createElement('script');

            Array.from(oldScript.attributes).forEach(attr => {
                newScript.setAttribute(attr.name, attr.value);
            });

            newScript.textContent = oldScript.textContent;

            oldScript.replaceWith(newScript);
        });
    }

    private applyHiddenFieldOverrides(): void {
        if (!this.formContainer) {
            return;
        }

        const attr = this.getAttribute('hidden-fields');
        if (!attr) {
            return;
        }

        let overrides: Record<string, string>;
        try {
            overrides = JSON.parse(attr);
        } catch {
            console.warn('[TruForm] Invalid JSON in hidden-fields attribute');
            return;
        }

        const normalizedOverrides = new Map(
            Object.entries(overrides).map(([k, v]) => [k.toLowerCase(), v])
        );

        const wrappers = this.formContainer.querySelectorAll<HTMLElement>('[data-group="hiddenFields"]');
        wrappers.forEach(wrapper => {
            const value = this.resolveHiddenFieldOverrideValue(wrapper, normalizedOverrides);
            if (value === undefined) {
                return;
            }

            const input = wrapper.querySelector<HTMLInputElement>('input[type="hidden"]');
            if (input) {
                input.value = value;
            }
        });
    }

    private resolveHiddenFieldOverrideValue(wrapper: HTMLElement, overrides: Map<string, string>): string | undefined {
        const keys = this.getHiddenFieldOverrideKeys(wrapper);

        for (const key of keys) {
            const value = overrides.get(key.toLowerCase());
            if (value !== undefined) {
                return value;
            }
        }

        return undefined;
    }

    private getHiddenFieldOverrideKeys(wrapper: HTMLElement): string[] {
        const keys: string[] = [];
        const label = wrapper.getAttribute('data-label');
        if (label) {
            keys.push(label);
        }

        const crmMapping = this.getHiddenFieldCrmMapping(wrapper);
        if (crmMapping?.type) {
            keys.push(crmMapping.type);
        }
        if (crmMapping?.systemName) {
            keys.push(crmMapping.systemName);
        }
        if (crmMapping?.mixedType && crmMapping?.type) {
            keys.push(`${crmMapping.mixedType}.${crmMapping.type}`);
        }
        if (crmMapping?.mixedType && crmMapping?.systemName) {
            keys.push(`${crmMapping.mixedType}.${crmMapping.systemName}`);
        }

        return keys;
    }

    private getHiddenFieldCrmMapping(wrapper: HTMLElement): CrmMappingInfo | null {
        const attr = wrapper.getAttribute('data-crm-field');
        if (!attr) {
            return null;
        }

        try {
            return Base64Utils.deserialize(attr) as CrmMappingInfo;
        } catch {
            console.warn('[TruForm] Failed to parse hidden field CRM mapping');
            return null;
        }
    }

    private cleanup(): void {
        if (this.formContainer) {
            this.formContainer.removeEventListener('submit', this.handleSubmit.bind(this));
        }
        if (this.recaptchaV2Container) {
            this.recaptchaV2Container.remove();
            this.recaptchaV2Container = null;
        }
    }

    public disconnectedCallback(): void {
        this.cleanup();
    }
}
